import { AxiosResponse } from 'axios';
import { TFunction } from 'i18next';
import { Button } from 'primereact/components/button/Button';
import { Column } from 'primereact/components/column/Column';
import { DataTable } from 'primereact/components/datatable/DataTable';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import '../CodeSidebar.scss';
import { Toolbar } from 'primereact/toolbar';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { SeverityEnum } from '../../../../shared/enum/SeverityEnum';
import { FileUtils } from '../../../../shared/utils/FileUtils';
import { Script } from '../../../models/Script';
import { AdminScriptsService } from '../../../services/AdminScriptsService';

interface States {
  scripts: Script[];
  scriptsWithLogs: Script[];
  isScriptsRefreshing: boolean;
  displayConfirmation: boolean;
  toDelete: string | null;
  loading: boolean;
  selectedScript: any;
  displayNewScriptConfirmation: boolean;
  menuFilter: string;

}

interface Props extends WithTranslation {
  deletedScript: any;
  reloadScripts: boolean;
  resetRefreshScriptsState: any;
  actionDeleteScript: boolean;
  actionDownloadScript: boolean;
  requestNavigation: any;
  loadFirstScript: any;
}

class CodeSidebarJS extends React.Component<Props, States> {

  private t: TFunction;
  private toast: Toast | null;
  private fileUpload: FileUpload | null;
  private readonly scriptService: AdminScriptsService;
  private scripts: any = [];

  constructor(props: Props) {
    super(props);
    this.scriptService = new AdminScriptsService();
    this.t = this.props.t;
    this.toast = null;
    this.fileUpload = null;

    this._rowClassOfScriptsList = this._rowClassOfScriptsList.bind(this);

    this.state = {
      scripts: [],
      scriptsWithLogs: [],
      isScriptsRefreshing: false,
      displayConfirmation: false,
      toDelete: null,
      loading: false,
      selectedScript: null,
      displayNewScriptConfirmation: false,
      menuFilter: '',
    };
  }


  async componentDidMount() {
    try {
      this.setState({loading: true})

      const scriptsWithLogsResponse = await this._getScriptsWithErrorLogs();
      this.setState({
        scriptsWithLogs: scriptsWithLogsResponse.data || [],
        loading: false
      })

    }
    catch (e) {
      console.error('Error in component mount:', e);
      this.setState({
        scriptsWithLogs: [],
        loading: false
      })
    }
    this.getScripts();
  }

  async componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any) {
    if (nextProps.reloadScripts) {
      this.setState({
        isScriptsRefreshing: true
      });
      this.getScripts();
    }
    if (nextProps.actionDeleteScript) {
      if (!this.state.selectedScript) {
        this.toast?.show({
          severity: SeverityEnum.ERROR,
          detail: `Please select a script !`,
        });
        return;
      }
      this.setState({ displayConfirmation: true, toDelete: this.state.selectedScript.fileName });
    }
    if (nextProps.actionDownloadScript) {
      this.handleDownload();
    }

    if (nextProps.resetRefreshScriptsState !== this.props.resetRefreshScriptsState) {
      const scriptsWithLogsResponse = await this._getScriptsWithErrorLogs();
      this.setState({
        scriptsWithLogs: scriptsWithLogsResponse.data || [],
      })
    }
  }


  /**
   * On download
   */
  handleDownload() {

    if (!this.state.selectedScript) {
      this.toast?.show({
        severity: SeverityEnum.ERROR,
        detail: `Please select a script !`,
      });
      return;
    }

    this.scriptService
      .downloadOne(this.state.selectedScript.fileName)
      .then((response: AxiosResponse<Blob>) => {
        FileUtils.downloadFile(response);
      });
  }

  /**
   * On upload
   */
  handleUpload(e: { files: File[] }) {
    if (!e.files) {
      return;
    }

    this.scriptService
      .uploadMany(e.files)
      .then(() => {
        this.toast?.show({
          severity: SeverityEnum.SUCCESS,
          /* detail: this.formService.t(this.t, "ui_admin_script_upload_toast_success", false), */
          detail: `Success - File uploaded`,
        });
        this.fileUpload?.clear();
        this.getScripts();
      })
      .catch((err: any) => {
        const message = err?.response?.data?.message;
        this.toast?.show({
          severity: SeverityEnum.ERROR,
          detail: `File NOT uploaded : ${message}`,
          /* detail: `${this.formService.t(this.t, "ui_admin_script_upload_toast_error", false)} : ${message}`, */
        });
      });

  }

  /**
   * New script
   */
  handleNewScript() {
    this.setState({ displayNewScriptConfirmation: false });
    this.resetEditorText();
  }

  /**
   * Delete script
   */
  handleDelete() {
    if (!this.state.toDelete) {
      return;
    }

    this.scriptService.deleteOne(this.state.toDelete).then(() => {
      this.toast?.show({
        severity: SeverityEnum.SUCCESS,
        detail: this.t("ui_admin_script_delete_toast_success"),
      });
      this.setState({ displayConfirmation: false, toDelete: null, selectedScript: null });
      this.getScripts();
      this.resetEditorText();
    });
  }


  resetEditorText() {
    this.props.deletedScript();
  }

  private async _getScriptsWithErrorLogs() {
    try {
      return await this.scriptService.getManyWithLogs();
    }
    catch (error) {
      console.error('Error fetching scripts with logs:', error);
      throw error;
    }
  }

  /**
   * Load scripts
   */
  getScripts() {
    this.scriptService
      .getMany()
      .then((response: AxiosResponse<Script[]>) => {
        const sortedScripts = response.data.sort((a, b) => a.fileName.localeCompare(b.fileName));
        const scriptToLoad = this.state.selectedScript ? this.state.selectedScript : response.data[0]

        this.scripts = sortedScripts;

        if (response.data.length > 0) {
          this.props.loadFirstScript(scriptToLoad.fileName)
          this.setState({ scripts: response.data,  selectedScript: scriptToLoad });
        }
      })
      .catch((error) => {
        console.error(error);
    });
  }

  actionBodyTemplate() {
    return (
      <React.Fragment>
        <Button
          type="button"
          tooltip="Add new script"
          /* label="New script"  */
          icon="pi pi-plus"
          style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
          className="p-button-success"
          onClick={() =>
            this.setState({ displayNewScriptConfirmation: true })
            //this.props.scriptToViewCallback(this.state.selectedScript.fileName);

          }
        />
        <FileUpload
          chooseLabel="Upload"
          uploadLabel="Ready to upload"
          mode="basic"
          ref={(el) => (this.fileUpload = el)}
          accept=".js"
          customUpload={true}
          /* multiple={true} */
          uploadHandler={(e: { files: File[] }) => this.handleUpload(e)}
          auto
          // emptyTemplate={() => <p className="p-m-0">{this.t("ui_admin_html_upload_content")}</p>}
        />

      </React.Fragment>
    );
  }

  renderDialogFooter() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          style={{ backgroundColor: '#0087b7', borderColor: '#0087b7' }}
          onClick={() => this.setState({ displayConfirmation: false })}
          className="p-button-danger"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          className="p-button-danger"
          style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
          onClick={() => this.handleDelete()}
          autoFocus
        />
      </div>
    );
  }

  /*
    Validate methods
  */
  validationWindowForNewScript() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          onClick={() => this.setState({ displayNewScriptConfirmation: false })}
          style={{ backgroundColor: '#0087b7', borderColor: '#0087b7' }}
          className="p-button-danger"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          onClick={() => this.handleNewScript()}
          autoFocus
          className="p-button-danger"
          style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
        />
      </div>
    );
  }

  private _rowClassOfScriptsList(script: Script) {
    const { scriptsWithLogs } = this.state;

    if (scriptsWithLogs && scriptsWithLogs.length > 0) {
      return {
        'row-error-highlight': scriptsWithLogs.some(
          (logScript) => logScript.fileName === script.fileName
        )
      };
    }

    return {};
  }

  render() {
    return (
      <>
        <Toast ref={(el) => this.toast = el}/>
        <InputText
          placeholder="Filter by name"
          className="menuFilter"
          value={this.state.menuFilter}
          onChange={(e: any) => {
            this.setState({
              menuFilter: e.target.value
            });
            if (e.target.value) {
              const scripts = this.scripts;
              const scriptsFiltered: any = [];
              for (const script of scripts) {
                if (script.fileName.includes(e.target.value)) {
                  scriptsFiltered.push(script);
                }
              }
              this.setState({
                scripts: scriptsFiltered
              });
            }
            else {
              this.setState({
                scripts: this.scripts
              });
            }
          }}
        />
        <DataTable
          value={this.state.scripts}
          tableClassName="table-custom scriptFilter"
          scrollable
          scrollHeight="100%"
          selection={this.state.selectedScript}
          rowClassName={ this._rowClassOfScriptsList }
          onSelectionChange={e => {
            this.setState(
              { selectedScript: e.value },
              () => { this.props.requestNavigation(e.value.fileName); }
            )}
          }
          dataKey="fileName"
          stateStorage="local"
          selectionMode="single"
          sortField="fileName"
          sortOrder={1}
        >
          <Column
            field="fileName"
            header="List of scripts"
          />

        </DataTable>
        <Toolbar
          left={() =>
            this.actionBodyTemplate()
          }
        />
        <Dialog
          header="Confirmation"
          visible={this.state.displayConfirmation}
          modal
          style={{ width: "350px" }}
          footer={this.renderDialogFooter()}
          onHide={() => this.setState({ displayConfirmation: false })}
        >
          <div className="confirmation-content">
            <i
              className="pi pi-exclamation-triangle p-mr-3"
              style={{ fontSize: "2rem" }}
            />
            <span>Are you sure you want to proceed?</span>
          </div>
        </Dialog>


        <div>
          <Dialog
            header="Confirmation"
            visible={this.state.displayNewScriptConfirmation}
            modal
            style={{ width: "350px" }}
            footer={this.validationWindowForNewScript()}
            onHide={() => this.setState({ displayNewScriptConfirmation: false })}
          >
            <div className="confirmation-content">
              <i
                className="pi pi-exclamation-triangle p-mr-3"
                style={{ fontSize: "2rem" }}
              />
              <span>Are you sure you want to create a new script?</span>

            </div>
          </Dialog>
        </div>
      </>
    );
  }
}

export default withTranslation()(CodeSidebarJS);
